/** The most dots the stepping control shows at once. */
export const MAX_DOTS = 5;

export type Dot = {
  /** The request this dot jumps to, counted from 1. */
  position: number;
  /** The dot for the request on show. */
  isCurrent: boolean;
  /** An end dot with more requests lying beyond it: drawn smaller to say so. */
  isEdge: boolean;
};

function clamp(value: number, lowest: number, highest: number): number {
  return Math.min(Math.max(value, lowest), highest);
}

/**
 * The dots to draw for the request on show: one per request while there are at most five, and
 * above five a window of five that slides with the current request, which sits in the middle of it
 * except near either end. An end dot is an edge dot when more requests lie beyond it.
 *
 * There is no limit on `total`. A `step` outside 1 to `total` is treated as the nearest request.
 */
export function dotWindow(step: number, total: number): Dot[] {
  if (total < 1) {
    return [];
  }
  const current = clamp(step, 1, total);
  const count = Math.min(total, MAX_DOTS);
  const first = clamp(current - Math.floor(MAX_DOTS / 2), 1, total - count + 1);
  const last = first + count - 1;
  return Array.from({ length: count }, (_, index) => {
    const position = first + index;
    const hasMoreBefore = position === first && first > 1;
    const hasMoreAfter = position === last && last < total;
    return { position, isCurrent: position === current, isEdge: hasMoreBefore || hasMoreAfter };
  });
}
